import type { ExternalFeed } from '../types/gateway';

const METADATA_KEYS = new Set([
  'cached_at',
  'source',
  'series_id',
  'provider',
  'timestamp',
  'updated_at',
  'retrieved_at',
  'as_of',
]);

export interface MarketIntelMetricSpec {
  sources: readonly string[];
  valueKeys: readonly string[];
  preferredInstruments?: readonly string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseNumeric(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function extractMetricNumber(value: unknown, keys: readonly string[]): number | null {
  const direct = parseNumeric(value);
  if (direct !== null) return direct;
  if (!isRecord(value)) return null;

  for (const key of keys) {
    const parsed = parseNumeric(value[key]);
    if (parsed !== null) return parsed;
  }

  return null;
}

export function findMetricFeed(
  feeds: readonly ExternalFeed[],
  spec: MarketIntelMetricSpec,
): ExternalFeed | undefined {
  const sourceCandidates = feeds.filter((feed) => spec.sources.includes(feed.source));
  if (sourceCandidates.length === 0) return undefined;

  for (const preferred of spec.preferredInstruments ?? []) {
    const match = sourceCandidates.find((feed) => feed.instrument === preferred);
    if (match) return match;
  }

  return sourceCandidates[0];
}

export function metricNumberFromFeed(
  feed: ExternalFeed | undefined,
  spec: MarketIntelMetricSpec,
): number | null {
  if (!feed) return null;
  return extractMetricNumber(feed.value, spec.valueKeys);
}

function formatPrimitive(value: unknown): string {
  if (typeof value === 'number') return value.toFixed(4);
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return String(value);
  return String(value);
}

export function formatExternalFeedValue(feed: ExternalFeed): string {
  const metric = metricNumberFromFeed(feed, {
    sources: [feed.source],
    valueKeys: metricKeysForSource(feed.source),
  });
  if (metric !== null) return formatMetricForSource(feed.source, metric);

  const value = feed.value;
  if (!isRecord(value)) return formatPrimitive(value);

  const entries = Object.entries(value).filter(([key]) => !METADATA_KEYS.has(key));
  if (entries.length === 0) return 'metadata only';

  return entries
    .slice(0, 2)
    .map(([key, entryValue]) => `${key}: ${formatPrimitive(entryValue)}`)
    .join(', ');
}

export function metricKeysForSource(source: string): readonly string[] {
  switch (source) {
    case 'vix':
      return ['value', 'vix', 'close', 'last', 'index'];
    case 'dxy':
      return ['value', 'dxy', 'close', 'last', 'index'];
    case 'fear_greed':
      return ['value', 'index', 'score'];
    case 'funding':
    case 'funding_rate':
    case 'funding_rate_agg':
      return ['funding_rate', 'rate', 'avg_funding_rate', 'weighted_funding_rate', 'value'];
    case 'oi':
    case 'open_interest':
      return ['open_interest_usd', 'oi_usd', 'open_interest', 'oi', 'oi_contracts', 'value'];
    case 'dvol':
      return ['dvol', 'value', 'index', 'atm_iv', 'iv', 'mark_iv'];
    default:
      return ['value'];
  }
}

export function formatMetricForSource(source: string, value: number): string {
  switch (source) {
    case 'funding':
    case 'funding_rate':
    case 'funding_rate_agg':
      return `${(value * 100).toFixed(4)}%`;
    case 'oi':
    case 'open_interest':
      if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
      if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
      return value.toFixed(0);
    case 'dxy':
      return value.toFixed(2);
    case 'fear_greed':
      return value.toFixed(0);
    case 'vix':
    case 'dvol':
      return value.toFixed(1);
    default:
      return value.toFixed(4);
  }
}
